import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import Modal from "../../../components/base/Modal";
import { Badge, Button, confirmDelete, notifyError, type Tone } from "../../../components/ui";
import { useBasesLocacionesStore, STAFF_ROLES, STAFF_STATUSES, type BaseStaff } from "../basesStore";
import { apiErrorMessage } from "../services/basesApi";
import { FilterSelect, FormField, Kpi, LoadBlock, PersonAvatar, SearchBox, SiteTree, Toolbar, inputCls, type TreeNode } from "../components/console";
import { TYPE_ICONS, STATUS_TONES } from "./BasesList";

// Personal asignado por base: árbol de bases a la izquierda, filtros por rol y
// estado, tabla con avatares de iniciales y alta/edición por la API.

const STAFF_TONES: Record<string, Tone> = { active: "success", vacation: "info", inactive: "neutral" };

type Draft = Omit<BaseStaff, "id">;

export default function BasesStaff() {
  const { t } = useTranslation("bases");
  const { bases, staff, status, error, hydrate, addStaff, updateStaff, removeStaff } = useBasesLocacionesStore();
  const [sel, setSel] = useState<string | null>(null);
  const [query, setQuery] = useState("");
  const [role, setRole] = useState<string>("");
  const [st, setSt] = useState<string>("");
  const [editing, setEditing] = useState<{ id?: string; draft: Draft } | null>(null);
  const [saving, setSaving] = useState(false);

  const baseName = (id: string) => bases.find((b) => b.id === id)?.name ?? "—";
  const nodes: TreeNode[] = useMemo(
    () => bases.map((b) => ({ id: b.id, label: b.name, icon: TYPE_ICONS[b.type], dotTone: STATUS_TONES[b.status], count: staff.filter((s) => s.base_id === b.id).length })),
    [bases, staff],
  );

  const rows = useMemo(() => {
    const q = query.trim().toLowerCase();
    return staff
      .filter((s) => (!sel || s.base_id === sel) && (!role || s.role === role) && (!st || s.status === st))
      .filter((s) => !q || [s.name, s.email, s.phone, baseName(s.base_id)].some((v) => String(v ?? "").toLowerCase().includes(q)))
      .sort((a, b) => a.name.localeCompare(b.name, "es"));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [staff, sel, role, st, query, bases]);

  const security = staff.filter((s) => s.role === "seguridad");
  const openNew = () => setEditing({ draft: { name: "", base_id: sel ?? bases[0]?.id ?? "", role: "seguridad", shift: "matutino", hours: "", email: "", phone: "", status: "active" } });

  const save = async () => {
    if (!editing) return;
    if (!editing.draft.name.trim() || !editing.draft.base_id) {
      notifyError(t("staff.required"));
      return;
    }
    setSaving(true);
    try {
      if (editing.id) await updateStaff(editing.id, editing.draft);
      else await addStaff(editing.draft);
      setEditing(null);
    } catch (err) {
      notifyError(apiErrorMessage(err, t("common.saveError")));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (s: BaseStaff) => {
    if (!(await confirmDelete({ title: t("staff.deleteTitle", { name: s.name }), cancelText: t("common.cancel"), confirmText: t("common.delete") }))) return;
    try {
      await removeStaff(s.id);
    } catch (err) {
      notifyError(apiErrorMessage(err, t("common.saveError")));
    }
  };

  const patch = (p: Partial<Draft>) => setEditing((e) => (e ? { ...e, draft: { ...e.draft, ...p } } : e));

  return (
    <div className="flex h-full w-full overflow-hidden">
      <SiteTree title={t("list.treeTitle")} allLabel={t("staff.allBases")} nodes={nodes} selected={sel} onSelect={setSel} />
      <div className="flex min-w-0 flex-1 flex-col overflow-hidden">
        <Toolbar>
          <div className="mr-2 flex items-center gap-2">
            <i className="ri-team-line text-lg text-primary-500" />
            <h1 className="text-sm font-semibold text-content">{sel ? baseName(sel) : t("staff.title")}</h1>
          </div>
          <SearchBox value={query} onChange={setQuery} placeholder={t("staff.search")} />
          <FilterSelect label={t("fields.role")} value={role} onChange={setRole} options={STAFF_ROLES.map((r) => ({ value: r, label: t(`roles.${r}`) }))} />
          <FilterSelect label={t("fields.status")} value={st} onChange={setSt} options={STAFF_STATUSES.map((r) => ({ value: r, label: t(`staffStatuses.${r}`) }))} />
          <div className="ml-auto">
            <Button size="sm" icon={<i className="ri-user-add-line" />} onClick={openNew} disabled={!bases.length}>
              {t("staff.new")}
            </Button>
          </div>
        </Toolbar>
        <div className="flex-1 overflow-y-auto p-4">
          <div className="mb-4 grid grid-cols-2 gap-3 md:grid-cols-4">
            <Kpi icon="ri-team-line" label={t("kpi.staff")} value={staff.length} />
            <Kpi icon="ri-shield-user-line" label={t("staff.security")} value={security.length} tone="info" />
            <Kpi icon="ri-user-follow-line" label={t("staffStatuses.active")} value={staff.filter((s) => s.status === "active").length} tone="success" />
            <Kpi icon="ri-sun-line" label={t("staffStatuses.vacation")} value={staff.filter((s) => s.status === "vacation").length} tone="warning" />
          </div>
          <LoadBlock status={status} error={error} onRetry={() => void hydrate()} empty={staff.length === 0} emptyIcon="ri-user-add-line" emptyTitle={t("staff.emptyTitle")} emptyText={t("staff.emptyText")}>
            <div className="overflow-x-auto rounded-xl border border-line bg-card">
              <table className="w-full min-w-[720px] text-sm">
                <thead className="bg-subtle text-left text-xs uppercase tracking-wide text-content-secondary">
                  <tr>
                    <th className="px-3 py-2">{t("fields.name")}</th>
                    <th className="px-3 py-2">{t("fields.role")}</th>
                    <th className="px-3 py-2">{t("fields.base")}</th>
                    <th className="px-3 py-2">{t("fields.shift")}</th>
                    <th className="px-3 py-2">{t("fields.contact")}</th>
                    <th className="px-3 py-2">{t("fields.status")}</th>
                    <th className="px-3 py-2" />
                  </tr>
                </thead>
                <tbody className="divide-y divide-line">
                  {rows.map((s) => (
                    <tr key={s.id} className="hover:bg-subtle">
                      <td className="px-3 py-2">
                        <span className="flex items-center gap-2 font-medium text-content">
                          <PersonAvatar name={s.name} size="sm" /> {s.name}
                        </span>
                      </td>
                      <td className="px-3 py-2 text-content-secondary">{t(`roles.${s.role}`, { defaultValue: s.role })}</td>
                      <td className="px-3 py-2 text-content-secondary">{baseName(s.base_id)}</td>
                      <td className="px-3 py-2 text-content-secondary">{s.shift ? t(`shifts.${s.shift}`, { defaultValue: s.shift }) : "—"} {s.hours ? <span className="text-xs text-content-muted">· {s.hours}</span> : null}</td>
                      <td className="px-3 py-2 text-xs text-content-secondary">
                        {s.email}
                        <br />
                        {s.phone}
                      </td>
                      <td className="px-3 py-2">
                        <Badge size="sm" tone={STAFF_TONES[s.status] ?? "neutral"} dot>
                          {t(`staffStatuses.${s.status}`, { defaultValue: s.status })}
                        </Badge>
                      </td>
                      <td className="px-3 py-2 text-right">
                        <Button size="xs" variant="ghost" iconOnly icon={<i className="ri-edit-line" />} aria-label={t("common.edit")} onClick={() => setEditing({ id: s.id, draft: { ...s } })} />
                        <Button size="xs" variant="ghost" iconOnly icon={<i className="ri-delete-bin-line" />} aria-label={t("common.delete")} onClick={() => void remove(s)} />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {rows.length === 0 ? <p className="py-8 text-center text-sm text-content-muted">{t("list.noMatches")}</p> : null}
            </div>
          </LoadBlock>
        </div>
      </div>

      {editing ? (
        <Modal
          title={editing.id ? t("staff.editTitle") : t("staff.new")}
          onClose={() => setEditing(null)}
          content={
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <FormField label={t("fields.name")} className="sm:col-span-2">
                <input className={inputCls} value={editing.draft.name} onChange={(e) => patch({ name: e.target.value })} />
              </FormField>
              <FormField label={t("fields.base")}>
                <select className={inputCls} value={editing.draft.base_id} onChange={(e) => patch({ base_id: e.target.value })}>
                  {bases.map((b) => (
                    <option key={b.id} value={b.id}>
                      {b.name}
                    </option>
                  ))}
                </select>
              </FormField>
              <FormField label={t("fields.role")}>
                <select className={inputCls} value={editing.draft.role} onChange={(e) => patch({ role: e.target.value })}>
                  {STAFF_ROLES.map((r) => (
                    <option key={r} value={r}>
                      {t(`roles.${r}`)}
                    </option>
                  ))}
                </select>
              </FormField>
              <FormField label={t("fields.shift")}>
                <select className={inputCls} value={editing.draft.shift ?? ""} onChange={(e) => patch({ shift: e.target.value })}>
                  {["matutino", "vespertino", "nocturno"].map((r) => (
                    <option key={r} value={r}>
                      {t(`shifts.${r}`)}
                    </option>
                  ))}
                </select>
              </FormField>
              <FormField label={t("fields.hours")}>
                <input className={inputCls} placeholder="Lun-Vie 07:00-15:00" value={editing.draft.hours ?? ""} onChange={(e) => patch({ hours: e.target.value })} />
              </FormField>
              <FormField label={t("fields.email")}>
                <input className={inputCls} value={editing.draft.email ?? ""} onChange={(e) => patch({ email: e.target.value })} />
              </FormField>
              <FormField label={t("fields.phone")}>
                <input className={inputCls} value={editing.draft.phone ?? ""} onChange={(e) => patch({ phone: e.target.value })} />
              </FormField>
              <FormField label={t("fields.status")}>
                <select className={inputCls} value={editing.draft.status} onChange={(e) => patch({ status: e.target.value as Draft["status"] })}>
                  {STAFF_STATUSES.map((r) => (
                    <option key={r} value={r}>
                      {t(`staffStatuses.${r}`)}
                    </option>
                  ))}
                </select>
              </FormField>
              <div className="flex justify-end gap-2 border-t border-line pt-4 sm:col-span-2">
                <Button variant="secondary" onClick={() => setEditing(null)}>
                  {t("common.cancel")}
                </Button>
                <Button loading={saving} onClick={() => void save()} icon={<i className="ri-save-line" />}>
                  {t("common.save")}
                </Button>
              </div>
            </div>
          }
        />
      ) : null}
    </div>
  );
}
